<template>
  <div>
    <div class="flex flex-col gap-3 mb-4 sm:mb-6 sm:flex-row sm:justify-between sm:items-center">
      <h2 class="text-xl sm:text-2xl font-bold text-gray-900">Subventionen verwalten</h2>
      <div class="flex gap-2 sm:gap-4">
        <input
          v-model="selectedYear"
          type="number"
          @change="loadSubsidies"
          class="w-24 sm:w-28 border border-gray-300 rounded-md px-3 py-2 text-black"
          placeholder="Jahr"
        />
        <button
          @click="openCreateModal"
          class="inline-flex flex-1 sm:flex-none items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700"
        >
          Neue Subvention
        </button>
      </div>
    </div>

    <div v-if="loading" class="text-center py-8">Laden...</div>
    <div v-else-if="error" class="rounded-md bg-red-50 p-4 mb-4">
      <p class="text-sm text-red-800">{{ error }}</p>
    </div>
    <div v-else>
      <div class="overflow-x-auto">
        <table class="min-w-full divide-y divide-gray-200">
          <thead class="bg-gray-50">
            <tr>
              <th class="hidden sm:table-cell px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ID</th>
              <th class="px-3 py-3 sm:px-6 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Wert</th>
              <th class="px-3 py-3 sm:px-6 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Datum</th>
              <th class="sticky right-0 bg-gray-50 px-3 py-3 sm:px-6 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Aktionen</th>
            </tr>
          </thead>
          <tbody class="bg-white divide-y divide-gray-200">
            <tr v-for="subsidy in subsidies" :key="subsidy.id">
              <td class="hidden sm:table-cell px-6 py-4 whitespace-nowrap text-sm text-gray-900">{{ subsidy.id }}</td>
              <td class="px-3 py-3 sm:px-6 sm:py-4 whitespace-nowrap text-sm text-gray-900">{{ subsidy.value }}</td>
              <td class="px-3 py-3 sm:px-6 sm:py-4 whitespace-nowrap text-sm text-gray-900">{{ formatDate(subsidy.date) }}</td>
              <td class="sticky right-0 bg-white px-3 py-3 sm:px-6 sm:py-4 whitespace-nowrap text-right text-sm font-medium">
                <button
                  @click="openEditModal(subsidy)"
                  class="text-indigo-600 hover:text-indigo-900 mr-3 sm:mr-4"
                >
                  Bearbeiten
                </button>
                <button
                  @click="confirmDelete(subsidy)"
                  class="text-red-600 hover:text-red-900"
                >
                  Löschen
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Create/Edit Modal -->
    <div v-if="showModal" class="fixed z-10 inset-0 overflow-y-auto" @click.self="closeModal">
      <div class="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"></div>
        <div class="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
          <form @submit.prevent="saveSubsidy">
            <div class="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
              <h3 class="text-lg leading-6 font-medium text-gray-900 mb-4">
                {{ editingSubsidy ? 'Subvention bearbeiten' : 'Neue Subvention' }}
              </h3>
              <div v-if="formError" class="rounded-md bg-red-50 p-3 mb-4">
                <p class="text-sm text-red-800">{{ formError }}</p>
              </div>
              <div class="space-y-4">
                <div>
                  <label class="block text-sm font-medium text-gray-700">Wert</label>
                  <input
                    v-model.number="formData.value"
                    type="number"
                    required
                    min="1"
                    class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 text-black focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                  />
                </div>
                <div>
                  <label class="block text-sm font-medium text-gray-700">Datum</label>
                  <input
                    v-model="formData.date"
                    type="date"
                    required
                    class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 text-black focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                  />
                </div>
              </div>
            </div>
            <div class="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
              <button
                type="submit"
                class="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-indigo-600 text-base font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:ml-3 sm:w-auto sm:text-sm"
              >
                Speichern
              </button>
              <button
                type="button"
                @click="closeModal"
                class="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
              >
                Abbrechen
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue'
import adminService, { type AdminSubsidy } from '@/services/AdminService'

interface SubsidyFormData {
  value: number
  date: string
}

// Helper function to get today's date as string
const getTodayDateString = (): string => {
  return new Date().toISOString().split('T')[0] as string
}

const subsidies = ref<AdminSubsidy[]>([])
const loading = ref(false)
const error = ref('')
const formError = ref('')
const showModal = ref(false)
const editingSubsidy = ref<AdminSubsidy | null>(null)
const selectedYear = ref(new Date().getFullYear().toString())
const formData = ref<SubsidyFormData>({
  value: 0,
  date: getTodayDateString()
})

const formatDate = (dateString: string) => {
  return new Date(dateString).toLocaleDateString('de-DE')
}

const loadSubsidies = async () => {
  loading.value = true
  error.value = ''
  try {
    subsidies.value = await adminService.getSubsidies(selectedYear.value)
  } catch (err: any) {
    error.value = err.response?.data?.error || 'Fehler beim Laden der Subventionen'
  } finally {
    loading.value = false
  }
}

const openCreateModal = () => {
  editingSubsidy.value = null
  formData.value = {
    value: 0,
    date: getTodayDateString()
  }
  showModal.value = true
}

const openEditModal = (subsidy: AdminSubsidy) => {
  editingSubsidy.value = subsidy
  formData.value = {
    value: subsidy.value,
    date: subsidy.date.split('T')[0] as string
  }
  showModal.value = true
}

const closeModal = () => {
  formError.value = ''
  showModal.value = false
  editingSubsidy.value = null
}

const saveSubsidy = async () => {
  formError.value = ''
  try {
    const payload = {
      value: formData.value.value,
      date: formData.value.date
    }
    
    if (editingSubsidy.value) {
      await adminService.updateSubsidy(editingSubsidy.value.id, payload)
    } else {
      await adminService.createSubsidy(payload)
    }
    closeModal()
    await loadSubsidies()
  } catch (err: any) {
    formError.value = err.response?.data?.error || 'Fehler beim Speichern der Subvention'
  }
}

const confirmDelete = async (subsidy: AdminSubsidy) => {
  if (confirm('Möchten Sie diese Subvention wirklich löschen?')) {
    try {
      await adminService.deleteSubsidy(subsidy.id)
      await loadSubsidies()
    } catch (err: any) {
      error.value = err.response?.data?.error || 'Fehler beim Löschen der Subvention'
    }
  }
}

onMounted(() => {
  loadSubsidies()
})
</script>

